import {
  W, H, CX, CY, APPLE_R, RIM, BOUNCE_H, BOUNCE_T, FALL_SPEED, BALL_SCREEN_Y, CAM_LERP,
  LEVEL_GAP, KEY_ROT_SPEED, MOVING_AFTER, REACTIVE_THRESHOLD, BONUS_TIME,
  GOAL_STEP, SQUASH_T, DEATH_T, SMASH_T, GREEN_KEEPS_STREAK,
} from './config';
import { Faller, Particle, Phase, Score } from './types';
import { createTower, ensureBuffer, growTo, cullAbove, Tower } from './tower';
import { FORWARD_BUFFER } from './config';
import { frontType } from './collision';
import { loadScore, savePoints, saveGoal, saveBest, passGain } from './score';
import { setupCanvas, readTheme, renderScene, fallerScreenY, Scene } from './renderer';
import { drawHUD, drawMenu, drawPause, drawGameOver } from './ui';
import { track } from './analytics';
import { attachInput, keyRotDir } from './input';
import {
  resumeAudio, playBounce, playPass, playGreen, playSmash, playDeath, playGoal,
} from './audio';

// ── sprite (ball skin): picked up from img/ if present, else procedural fallback ──
const ballGlob = import.meta.glob('../img/ball1.png', { eager: true, query: '?url', import: 'default' });
const ballUrl = (Object.values(ballGlob)[0] as string | undefined) || null;
let sprite: HTMLImageElement | null = null;
if (ballUrl) {
  const img = new Image();
  img.onload = () => { sprite = img; };
  img.onerror = () => { sprite = null; };
  img.src = ballUrl;
}

const canvas = document.getElementById('game') as HTMLCanvasElement;
const ctx = setupCanvas(canvas);
const theme = readTheme();

function fitCanvas(): void {
  const scale = Math.min(window.innerWidth / W, window.innerHeight / H);
  canvas.style.width = Math.round(W * scale) + 'px';
  canvas.style.height = Math.round(H * scale) + 'px';
}
fitCanvas();
window.addEventListener('resize', fitCanvas);
window.addEventListener('orientationchange', fitCanvas);

// ── session state ──
let phase: Phase = 'menu';
let tower: Tower;
let faller: Faller;
let towerAngle = 0;
let camWorldY = 0;
let score: Score = loadScore();
let particles: Particle[] = [];
let gt = 0;                 // game time (ms), advances only while running → pause-safe
let squashStart = -1;
let deathStart = -1;
let isRecord = false;

const bonusActive = (): boolean => score.bonusUntil > gt;
const ring = (lvl: number) => tower.rings.get(lvl);

function anchorWorldY(): number {
  if (faller.state === 'bouncing') {
    const r = ring(faller.restIndex);
    return (r ? r.worldY : 0) + RIM - APPLE_R;
  }
  return faller.worldY;
}

function startRun(): void {
  tower = createTower(Math.floor(Math.random() * 1e9) + 1, MOVING_AFTER);
  faller = {
    worldY: RIM - APPLE_R, vy: 0, state: 'bouncing',
    restIndex: 0, targetIndex: 0, bouncePhase: 0, squashStart: -1, reactive: false,
  };
  towerAngle = 0;
  gt = 0;
  squashStart = -1;
  deathStart = -1;
  isRecord = false;
  particles = [];
  score.run = 0;
  score.passInDive = 0;
  score.passStreak = 0;
  score.bonusUntil = 0;
  camWorldY = anchorWorldY() + (CY - BALL_SCREEN_Y);
  phase = 'running';
  resumeAudio();
  track('game_start');
}

// ── scoring helpers ──
function addEvent(gain: number): void {
  score.event += gain;
  if (score.event >= score.goal) { score.goal += GOAL_STEP; saveGoal(score); playGoal(); }
}

function doPass(): void {
  score.passInDive++;
  score.passStreak++;
  const gain = passGain(score.passInDive, bonusActive());
  score.run += gain;
  addEvent(gain);
  playPass(score.passInDive);
  if (score.passStreak >= REACTIVE_THRESHOLD) faller.reactive = true;
}

function beginFall(targetIndex: number): void {
  faller.state = 'falling';
  faller.vy = FALL_SPEED;
  faller.targetIndex = targetIndex;
  squashStart = gt;
}

function land(lvl: number): void {
  faller.state = 'bouncing';
  faller.restIndex = lvl;
  faller.targetIndex = lvl;
  faller.bouncePhase = 0;
  faller.worldY = ring(lvl)!.worldY + RIM - APPLE_R;
  score.passInDive = 0;
  score.passStreak = 0;
  faller.reactive = false;
  playBounce();
}

// Green landing: bounce once like a normal platform, but start folding the disc
// and turn on ×2. The pass is counted on the next bottom-contact (removing → gap),
// so the streak is kept (ТЗ 3.5.6). Does NOT reset the combo like a safe landing.
function landGreen(lvl: number): void {
  foldRing(lvl, 'green');
  score.bonusUntil = gt + BONUS_TIME;
  faller.state = 'bouncing';
  faller.restIndex = lvl;
  faller.targetIndex = lvl;
  faller.bouncePhase = 0;
  faller.worldY = ring(lvl)!.worldY + RIM - APPLE_R;
  if (!GREEN_KEEPS_STREAK) { score.passInDive = 0; score.passStreak = 0; }
  playGreen();
}

function foldRing(lvl: number, kind: 'green' | 'smash'): void {
  const r = ring(lvl);
  if (r) { r.removing = kind; r.removeStart = gt; }
}

function die(): void {
  faller.state = 'dead';
  deathStart = gt;
  const y = fallerScreenY(faller.worldY, camWorldY);
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    particles.push({
      x: CX, y, vx: Math.cos(a) * 240, vy: Math.sin(a) * 240 - 80,
      life: DEATH_T, max: DEATH_T, color: theme.ball, r: 6,
    });
  }
  playDeath();
}

function smashParticles(): void {
  const y = fallerScreenY(faller.worldY, camWorldY);
  for (let i = 0; i < 5; i++) {
    const a = Math.random() * Math.PI * 2;
    particles.push({
      x: CX, y, vx: Math.cos(a) * 200, vy: Math.sin(a) * 200,
      life: SMASH_T, max: SMASH_T, color: theme.reactive, r: 5,
    });
  }
}

// Evaluate the ring the faller is contacting (from a bounce-bottom or a fall).
// Returns true if the faller is now falling (passed/through), false if it stays/lands.
function evaluate(lvl: number): boolean {
  const r = ring(lvl);
  if (!r) { return true; }                 // safety: no platform → keep falling
  if (r.removing) { doPass(); beginFall(lvl + 1); return true; }  // folded green → pass through
  const type = frontType(r, towerAngle, gt / 1000);

  if (faller.reactive) {
    if (type === 'gap') {                   // nothing to smash → reactive ends, clean pass
      faller.reactive = false;
      score.passStreak = 0;
      doPass();
      beginFall(lvl + 1);
      return true;
    }
    foldRing(lvl, 'smash');                 // punch through any solid platform
    smashParticles();
    playSmash();
    if (type === 'red') score.bonusUntil = 0;
    beginFall(lvl + 1);
    return true;
  }

  if (type === 'gap') { doPass(); beginFall(lvl + 1); return true; }
  if (type === 'green') { landGreen(lvl); return false; }   // bounce, fold, then pass next contact
  if (type === 'red') { die(); return false; }
  // safe → land / keep bouncing
  land(lvl);
  return false;
}

// ── per-step update ──
function update(dt: number, dtMs: number): void {
  gt += dtMs;

  // rotation
  const kd = keyRotDir();
  if (kd !== 0) towerAngle += kd * KEY_ROT_SPEED * dt;

  if (faller.state === 'dead') {
    if (gt - deathStart >= DEATH_T) finishRun();
  } else if (faller.state === 'bouncing') {
    faller.bouncePhase += dtMs;
    if (faller.bouncePhase >= BOUNCE_T) {
      faller.bouncePhase -= BOUNCE_T;
      evaluate(faller.restIndex);                // bottom of bounce → check
    }
    if (faller.state === 'bouncing') {
      const frac = faller.bouncePhase / BOUNCE_T;
      const rest = (ring(faller.restIndex)?.worldY ?? 0) + RIM - APPLE_R;
      faller.worldY = rest - BOUNCE_H * Math.sin(frac * Math.PI);
    }
    ensureBuffer(tower, faller.restIndex, MOVING_AFTER);
    cullAbove(tower, Math.floor((camWorldY - CY) / LEVEL_GAP) - 2);
  } else { // falling / reactive
    // safety: a dive longer than the buffer must never out-run generation
    if (faller.targetIndex + 2 > tower.generatedTo) {
      growTo(tower, faller.targetIndex + FORWARD_BUFFER, MOVING_AFTER);
    }
    faller.worldY += faller.vy * dt;
    let guard = 0;
    // resolve every ring crossed this step (a fast reactive dive can cross several)
    while (faller.state === 'falling' && ring(faller.targetIndex)
           && faller.worldY >= ring(faller.targetIndex)!.worldY + RIM - APPLE_R && guard++ < 40) {
      evaluate(faller.targetIndex);
    }
  }

  // camera follow (eased; lag during fast falls makes the ball dip then recover)
  const camTarget = anchorWorldY() + (CY - BALL_SCREEN_Y);
  camWorldY += (camTarget - camWorldY) * Math.min(1, CAM_LERP * dt);

  // particles
  for (const p of particles) {
    p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 900 * dt; p.life -= dtMs;
  }
  if (particles.length) particles = particles.filter((p) => p.life > 0);
}

function finishRun(): void {
  if (score.run > score.best) { score.best = score.run; isRecord = true; saveBest(score); }
  savePoints(score);
  saveGoal(score);
  if (isRecord) track('new_best', { score: score.run });
  track('game_over', { result: 'lose', score: score.run });
  phase = 'gameover';
}

function squashY(): number {
  if (squashStart < 0) return 1;
  const p = (gt - squashStart) / SQUASH_T;
  if (p >= 1) { squashStart = -1; return 1; }
  return 1 - 0.1 * Math.sin(p * Math.PI);
}

// ── input wiring ──
attachInput(canvas, {
  getPhase: () => phase,
  rotateBy: (d) => { if (phase === 'running') towerAngle += d; },
  pauseToggle: () => {
    if (phase === 'running') phase = 'paused';
    else if (phase === 'paused') { phase = 'running'; resumeAudio(); }
  },
  press: (name) => {
    resumeAudio();
    if (name === 'play' || name === 'again') startRun();
    else if (name === 'resume') phase = 'running';
    else if (name === 'menu') { savePoints(score); phase = 'menu'; }
    else if (name === 'close') phase = 'paused';
    else if (name === 'home') { savePoints(score); location.href = document.body.dataset.hubUrl ?? '/'; }
  },
});

document.addEventListener('touchstart', resumeAudio, { once: true });
document.addEventListener('click', resumeAudio, { once: true });
document.addEventListener('visibilitychange', () => {
  if (document.hidden) { if (phase === 'running') phase = 'paused'; savePoints(score); }
  else resumeAudio();
});

// ── RAF loop with fixed-step accumulator (smooth, deterministic logic) ──
const STEP_MS = 1000 / 120;
let lastTime = 0;
let acc = 0;

function frame(now: number): void {
  try {
    const dtMs = lastTime ? Math.min(now - lastTime, 50) : 0;
    lastTime = now;
    if (phase === 'running') {
      acc += dtMs;
      while (acc >= STEP_MS) { update(STEP_MS / 1000, STEP_MS); acc -= STEP_MS; }
    } else {
      acc = 0;
    }

    const scene: Scene = {
      tower, faller, camWorldY, towerAngle, t: gt / 1000, now: gt,
      squashY: squashY(), particles, sprite, score, phase,
    };
    if (phase !== 'menu') renderScene(ctx, theme, scene);
    else { ctx.fillStyle = theme.pageBg; ctx.fillRect(0, 0, W, H); }

    if (phase === 'running' || phase === 'paused') drawHUD(ctx, theme, score, gt);
    if (phase === 'menu') drawMenu(ctx, theme, score);
    else if (phase === 'paused') drawPause(ctx, theme);
    else if (phase === 'gameover') drawGameOver(ctx, theme, score, isRecord);
  } catch (e) {
    console.error('[helix] loop error', e);
  }
  requestAnimationFrame(frame);
}

if (location.search.indexOf('play') >= 0) {             // dev: ?play auto-starts a run
  startRun();
  const m = location.search.match(/[?&]a=([-0-9.]+)/);  // dev: &a=<rad> presets the tower angle
  if (m) towerAngle = parseFloat(m[1]);
}

requestAnimationFrame(frame);
